/**
 * Utility functions for truncating HTML content while preserving formatting
 */

/**
 * Truncates HTML content to approximately the specified number of lines
 * by limiting character count and preserving HTML structure
 */
export const truncateHtmlContent = (
  htmlContent: string, 
  maxLines: number = 2,
  charactersPerLine: number = 80
): { 
  truncatedHtml: string; 
  isTruncated: boolean; 
} => {
  if (!htmlContent || htmlContent.trim() === '' || htmlContent === '<p><br></p>') {
    return { truncatedHtml: htmlContent, isTruncated: false };
  }

  // Calculate approximate character limit based on lines
  const maxCharacters = maxLines * charactersPerLine;

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  // Get text content to check actual length
  const textContent = tempDiv.textContent || tempDiv.innerText || '';
  
  if (textContent.length <= maxCharacters) {
    return { truncatedHtml: htmlContent, isTruncated: false };
  }

  // Simple approach: truncate the text content and rebuild minimal HTML
  const truncatedText = textContent.substring(0, maxCharacters);
  
  // Find the last complete word
  const lastSpaceIndex = truncatedText.lastIndexOf(' ');
  const finalText = lastSpaceIndex > maxCharacters * 0.8 
    ? truncatedText.substring(0, lastSpaceIndex)
    : truncatedText;

  // Check if original content had formatting by looking for common tags
  const hasFormatting = /<(strong|em|b|i|u|ul|ol|li)/.test(htmlContent);
  
  if (hasFormatting) {
    // Try to preserve some basic formatting
    const truncatedHtml = preserveBasicFormatting(htmlContent, finalText.length);
    return { truncatedHtml, isTruncated: true };
  }

  // Return as simple paragraph if no special formatting
  return { 
    truncatedHtml: `<p>${finalText}</p>`, 
    isTruncated: true 
  };
};

/**
 * Attempts to preserve basic HTML formatting while truncating
 */
function preserveBasicFormatting(htmlContent: string, maxLength: number): string {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  let currentLength = 0;
  let result = '';
  
  function processNode(node: Node): boolean {
    if (currentLength >= maxLength) return false;
    
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      const remainingLength = maxLength - currentLength;
      
      if (text.length <= remainingLength) {
        result += text;
        currentLength += text.length;
        return true;
      } else {
        const truncatedText = text.substring(0, remainingLength);
        const lastSpace = truncatedText.lastIndexOf(' ');
        const finalText = lastSpace > remainingLength * 0.8 
          ? truncatedText.substring(0, lastSpace)
          : truncatedText;
        
        result += finalText;
        currentLength = maxLength;
        return false;
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName.toLowerCase();
      
      // Include common formatting tags
      if (['p', 'strong', 'em', 'b', 'i', 'u'].includes(tagName)) {
        result += `<${tagName}>`;
        
        for (let child of Array.from(node.childNodes)) {
          if (!processNode(child)) break;
        }
        
        result += `</${tagName}>`;
      } else {
        // For other elements, just process children
        for (let child of Array.from(node.childNodes)) {
          if (!processNode(child)) break;
        }
      }
    }
    
    return currentLength < maxLength;
  }
  
  for (let child of Array.from(tempDiv.childNodes)) {
    if (!processNode(child)) break;
  }
  
  return result || '<p></p>';
}